import type {
  CoverageParametersCoverageAmountType,
  IncomeLineIncomeType,
  RecommendedAmountLabelLabelType,
  RiskRiskType,
  SocialSupportCalculationMode,
} from "Infrastructure/ApiZfp/Schema";

// Bridge maps from the new API's lower-snake-case enum values to the existing PascalCase Czech
// translation keys, same convention as Infrastructure/Utils/AssetUtils.ts.

export const COVERAGE_AMOUNT_TYPE_RESOURCE_KEY: Record<CoverageParametersCoverageAmountType, "ClientIncomes" | "FamilyExpenses" | "Other"> =
  {
    client_incomes: "ClientIncomes",
    family_expenses: "FamilyExpenses",
    other: "Other",
  };

export const SOCIAL_SUPPORT_CALCULATION_MODE_RESOURCE_KEY: Record<SocialSupportCalculationMode, "Individual" | "Average"> = {
  individual: "Individual",
  average: "Average",
};

// The new API collapses the old per-degree disability incomes and the separate orphan/widow
// pension incomes into single `disability` / `orphan_or_widow_pension` buckets.
export const INCOME_LINE_TYPE_RESOURCE_KEY: Record<
  IncomeLineIncomeType,
  "Employment" | "SelfEmployment" | "Rental" | "Parental" | "Pension" | "Disability" | "OrphanOrWidowPension" | "Other"
> = {
  employment: "Employment",
  self_employment: "SelfEmployment",
  rental: "Rental",
  parental: "Parental",
  pension: "Pension",
  disability: "Disability",
  orphan_or_widow_pension: "OrphanOrWidowPension",
  other: "Other",
};

export const RECOMMENDED_AMOUNT_LABEL_TYPE_RESOURCE_KEY: Record<
  RecommendedAmountLabelLabelType,
  "Default" | "ForFirstMonth" | "ForSecondMonth" | "FromThirdMonth"
> = {
  default: "Default",
  for_first_month: "ForFirstMonth",
  for_second_month: "ForSecondMonth",
  from_third_month: "FromThirdMonth",
};

export const RISK_TYPE_RESOURCE_KEY: Record<
  RiskRiskType,
  | "DeathConstant"
  | "DeathDecreasingLinear"
  | "DeathDecreasingAnnuity"
  | "AccidentalDeath"
  | "Disability3rdDegreeConstant"
  | "Disability2ndDegreeConstant"
  | "Disability1stDegreeConstant"
  | "Disability3rdDegreeDecreasingLinear"
  | "Disability2ndDegreeDecreasingLinear"
  | "Disability1stDegreeDecreasingLinear"
  | "Disability3rdDegreeDecreasingAnnuity"
  | "Disability2ndDegreeDecreasingAnnuity"
  | "Disability1stDegreeDecreasingAnnuity"
  | "CriticalIllnessDecreasingLinear"
  | "CriticalIllnessDecreasingAnnuity"
  | "PermanentInjuryFromLowestPercent"
  | "PermanentInjuryFrom10Percent"
  | "CriticalIllnessConstant"
  | "WorkIncapacityFrom15thDay"
  | "WorkIncapacityFrom29thDay"
  | "WorkIncapacityFrom57thDay"
  | "WorkIncapacityFrom85thDay"
  | "NursingAllowanceFrom15thDay"
  | "NursingAllowanceFrom29thDay"
  | "NursingAllowanceFrom57thDay"
  | "NursingAllowanceFrom85thDay"
  | "InjuryTreatment"
  | "HospitalizationAccident"
  | "HospitalizationAccidentAndIllness"
> = {
  death_constant: "DeathConstant",
  death_decreasing_linear: "DeathDecreasingLinear",
  death_decreasing_annuity: "DeathDecreasingAnnuity",
  accidental_death: "AccidentalDeath",
  disability_3rd_degree_constant: "Disability3rdDegreeConstant",
  disability_2nd_degree_constant: "Disability2ndDegreeConstant",
  disability_1st_degree_constant: "Disability1stDegreeConstant",
  disability_3rd_degree_decreasing_linear: "Disability3rdDegreeDecreasingLinear",
  disability_2nd_degree_decreasing_linear: "Disability2ndDegreeDecreasingLinear",
  disability_1st_degree_decreasing_linear: "Disability1stDegreeDecreasingLinear",
  disability_3rd_degree_decreasing_annuity: "Disability3rdDegreeDecreasingAnnuity",
  disability_2nd_degree_decreasing_annuity: "Disability2ndDegreeDecreasingAnnuity",
  disability_1st_degree_decreasing_annuity: "Disability1stDegreeDecreasingAnnuity",
  critical_illness_decreasing_linear: "CriticalIllnessDecreasingLinear",
  critical_illness_decreasing_annuity: "CriticalIllnessDecreasingAnnuity",
  permanent_injury_from_lowest_percent: "PermanentInjuryFromLowestPercent",
  permanent_injury_from_10_percent: "PermanentInjuryFrom10Percent",
  critical_illness_constant: "CriticalIllnessConstant",
  work_incapacity_from_15th_day: "WorkIncapacityFrom15thDay",
  work_incapacity_from_29th_day: "WorkIncapacityFrom29thDay",
  work_incapacity_from_57th_day: "WorkIncapacityFrom57thDay",
  work_incapacity_from_85th_day: "WorkIncapacityFrom85thDay",
  nursing_allowance_from_15th_day: "NursingAllowanceFrom15thDay",
  nursing_allowance_from_29th_day: "NursingAllowanceFrom29thDay",
  nursing_allowance_from_57th_day: "NursingAllowanceFrom57thDay",
  nursing_allowance_from_85th_day: "NursingAllowanceFrom85thDay",
  injury_treatment: "InjuryTreatment",
  hospitalization_accident: "HospitalizationAccident",
  hospitalization_accident_and_illness: "HospitalizationAccidentAndIllness",
};

// `ExpenseLine.expenseType` is a plain `string` on the schema (not a generated literal union),
// so this map is keyed defensively rather than exhaustively checked by the compiler.
export const FAMILY_EXPENSE_TYPE_RESOURCE_KEY: Record<
  string,
  "Housing" | "Consumption" | "LifeStyle" | "Loan" | "Insurance" | "FinancialProduct" | "Other"
> = {
  housing: "Housing",
  consumption: "Consumption",
  life_style: "LifeStyle",
  loan: "Loan",
  insurance: "Insurance",
  "financial-product": "FinancialProduct",
  other: "Other",
};
